import React, { useContext, useEffect, useState } from "react";
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField } from "@mui/material";
import { useIntl } from "react-intl";
import UserContext from "context/UserContext";
import RevaText from "components/common/RevaText";
import performRequest from "network/perform-request";
import { formatFinancialAmount } from "../financial/financial-helpers";

export const VOID_REASON_MIN_LENGTH = 10;

const invoiceLabel = (invoice) => invoice.description || invoice.invoice_items?.[0]?.description || `#${invoice.id}`;

const VoidInvoiceDialog = ({ invoices, currency, voidRoute, onClose, onVoided }) => {
  const { user } = useContext(UserContext);
  const intl = useIntl();
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const tooShort = reason.trim().length < VOID_REASON_MIN_LENGTH;
  const open = invoices.length > 0;
  const total = invoices.reduce((sum, invoice) => sum + Number(invoice.final_amount || 0), 0);

  useEffect(() => {
    setReason("");
    setError(null);
  }, [open]);

  const submit = () => {
    setSaving(true);
    setError(null);
    performRequest(
      "POST",
      voidRoute,
      { invoice_ids: invoices.map((invoice) => invoice.id), reason: reason.trim() },
      true,
      user,
    )
      .then(() => onVoided())
      .catch((requestError) =>
        setError(requestError?.response?.data?.message || intl.formatMessage({ id: "gestion.error.generic" })),
      )
      .finally(() => setSaving(false));
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{intl.formatMessage(
          { id: invoices.length > 1 ? "gestion.playerFinance.voidManyTitle" : "gestion.playerFinance.voidTitle" },
          { count: invoices.length },
        )}</DialogTitle>
      <DialogContent>
        {open && (
          <Stack spacing={2} sx={{ pt: 1 }}>
            <RevaText variant="body2">
              {invoices.length > 1
                ? intl.formatMessage(
                    { id: "gestion.playerFinance.voidManyDescription" },
                    { count: invoices.length, amount: formatFinancialAmount(total, currency) },
                  )
                : intl.formatMessage(
                    { id: "gestion.playerFinance.voidDescription" },
                    { description: invoiceLabel(invoices[0]), amount: formatFinancialAmount(total, currency) },
                  )}
            </RevaText>
            <TextField
              autoFocus
              multiline
              minRows={3}
              label={intl.formatMessage({ id: "gestion.playerFinance.voidReason" })}
              helperText={intl.formatMessage(
                { id: "gestion.playerFinance.voidReasonHelp" },
                { min: VOID_REASON_MIN_LENGTH },
              )}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              inputProps={{ maxLength: 1000 }}
            />
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {intl.formatMessage({ id: "gestion.accountFinance.cancel" })}
        </Button>
        <Button variant="contained" color="error" onClick={submit} disabled={saving || tooShort}>
          {intl.formatMessage({ id: "gestion.playerFinance.voidConfirm" }, { count: invoices.length })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VoidInvoiceDialog;
